import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Link, withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { parse, stringify } from 'qs';
import { Helmet } from 'react-helmet';
import Modal from 'react-bootstrap/Modal';
import { connect } from 'react-redux';
import { getItems } from './marketplaceActions';
import MarketplaceViewItem from './marketplaceViewItem';
import ItemViewContent from '../item/itemViewContent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faShoppingBasket, faTimes } from '@fortawesome/free-solid-svg-icons';

const MarketplaceAdminView = ({ marketplace, location, history, getItems, items, loading, error }) => {
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!marketplace) {
      return;
    }

    getItems({ marketplaceId: marketplace.id, includeHidden: true });
  }, [marketplace]);

  function onCategoryChange(newCategory) {
    if (newCategory === activeCategory) {
      return;
    }

    setActiveCategory(newCategory);
    getItems({ marketplaceId: marketplace.id, category: newCategory });
  }

  function handleKeyUpEvent(e) {
    switch (e.key) {
      case 'ArrowRight':
        nextItemModal();
        break;
      case 'ArrowLeft':
        previousItemModal();
        break;
    }
  }

  function nextItemModal() {
    const searchParams = parse(location.search, { ignoreQueryPrefix: true });
    if (!searchParams.itemId || !items || items.length <= 1) {
      return;
    }

    const currentItemIndex = items.findIndex((item) => item.id.toString() === searchParams.itemId);
    if (currentItemIndex < items.length - 1) {
      openItemModal(items[currentItemIndex + 1]);
    }
  }

  function previousItemModal() {
    const searchParams = parse(location.search, { ignoreQueryPrefix: true });
    if (!searchParams.itemId || !items || items.length <= 1) {
      return;
    }

    const currentItemIndex = items.findIndex((item) => item.id.toString() === searchParams.itemId);
    if (currentItemIndex > 0) {
      openItemModal(items[currentItemIndex - 1]);
    }
  }

  function openItemModal(item) {
    const oldPath = history.location.pathname;
    const searchParams = parse(location.search, { ignoreQueryPrefix: true });
    searchParams.itemId = item.id;
    const newQueryString = stringify(searchParams, { ignoreQueryPrefix: true });

    history.replace({
      pathname: oldPath,
      search: newQueryString,
    });
  }

  function closeItemModal() {
    const oldPath = history.location.pathname;
    const searchParams = parse(location.search, { ignoreQueryPrefix: true });
    delete searchParams.itemId;
    const newQueryString = stringify(searchParams, { ignoreQueryPrefix: true });

    history.replace({
      pathname: oldPath,
      search: newQueryString,
    });
  }

  function getCategoriesPanel() {
    const categories = marketplace.categories.map((category, index) => {
      const categoryClasses = classNames({
        active: activeCategory === category,
        'marketplace-view__categories-category text-primary': true,
      });

      return (
        <li key={index} className="list-inline-item m-r-2">
          <small>
            <strong>
              <a className={categoryClasses} href="javascript: void(0)" onClick={() => onCategoryChange(category)}>
                {category}
              </a>
            </strong>
          </small>
        </li>
      );
    });

    const allCategoryClasses = classNames({
      active: !activeCategory,
      'marketplace-view__categories-category': true,
    });

    return (
      <div className="m-b-3 text-center">
        <ul className="list-unstyled list-inline m-b-0">
          <li className="list-inline-item m-r-2">
            <small>
              <strong>
                <a className={allCategoryClasses} href="javascript: void(0)" onClick={() => onCategoryChange(null)}>
                  <FormattedMessage id={'marketplace.view.categories.all'} />
                </a>
              </strong>
            </small>
          </li>
          {categories}
        </ul>
      </div>
    );
  }

  function getItemsSection(marketplace) {
    if (items && items.length) {
      const nonHiddenItems = items.filter((i) => !i.isPrivate);
      const hiddenItems = items.filter((i) => i.isPrivate);

      return (
        <div className="row">
          {nonHiddenItems.map((item, index) => (
            <div key={index} className="col-6 col-md-6 col-xl-4 marketplace-view-item-col">
              <MarketplaceViewItem marketplace={marketplace} item={item} onItemClick={() => openItemModal(item)} />
            </div>
          ))}
          {hiddenItems.map((item, index) => (
            <div key={index} className="col-6 col-md-6 col-xl-4 marketplace-view-item-col">
              <MarketplaceViewItem marketplace={marketplace} item={item} onItemClick={() => openItemModal(item)} />
            </div>
          ))}
        </div>
      );
    } else if (items && items.length === 0) {
      return (
        <p>
          <FormattedMessage id={'marketplace.view.items.no.items.found'} />
        </p>
      );
    } else {
      return '';
    }
  }

  function getMarketplaceSection() {
    const items = getItemsSection(marketplace);

    const categories = marketplace.categories && marketplace.categories.length ? getCategoriesPanel() : '';

    const itemsLoader = loading ? (
      <div className="text-center m-t-5 m-b-5">
        <div className="b-loader" />
      </div>
    ) : (
      ''
    );

    return (
      <div className="row">
        <Helmet>
          <title>{marketplace.name}</title>
          <meta name="description" content={marketplace.description} />
        </Helmet>

        <div className="col-sm-12 marketplace-view">
          <div className="row m-b-3">
            <div className="col-12">
              <h3 className="text-center marketplace-view__title">{marketplace.name}</h3>
              <p className="text-center marketplace-view__description">{marketplace.description}</p>
              {categories}
            </div>
          </div>

          {itemsLoader}

          {items}
        </div>
      </div>
    );
  }

  const errorMessage = error ? (
    <div className="row m-t-5 m-b-5">
      <div className="col-12">
        <div className="alert alert-error">{error}</div>
      </div>
    </div>
  ) : (
    ''
  );

  const loader = loading ? (
    <div className="text-center m-t-5 m-b-5">
      <div className="b-loader" />
    </div>
  ) : (
    ''
  );

  const marketplaceContent = marketplace ? getMarketplaceSection() : '';

  const queryParams = parse(location.search, { ignoreQueryPrefix: true });
  const modalItem =
    queryParams.itemId && items ? items.find((item) => item.id.toString() === queryParams.itemId) : null;

  const nextArrow =
    modalItem && items[items.length - 1].id !== modalItem.id ? (
      <div onClick={nextItemModal} className="b-modal-next">
        <FontAwesomeIcon icon={faChevronRight} />
      </div>
    ) : (
      ''
    );

  const previousArrow =
    modalItem && items[0].id !== modalItem.id ? (
      <div onClick={previousItemModal} className="b-modal-previous">
        <FontAwesomeIcon icon={faChevronLeft} />
      </div>
    ) : (
      ''
    );

  return (
    <div className="panel" onKeyUp={handleKeyUpEvent}>
      <div className="panel__corner">
        <FontAwesomeIcon icon={faShoppingBasket} />
      </div>
      <h6 className="panel__title">
        <FormattedMessage id={'marketplace.view.panel.title'} />
      </h6>

      {loader}

      {errorMessage}

      {marketplaceContent}

      <Modal show={!!modalItem} onHide={closeItemModal} dialogClassName="container-size">
        <div onClick={closeItemModal} className="b-modal-close">
          <FontAwesomeIcon icon={faTimes} />
        </div>

        {nextArrow}
        {previousArrow}

        <div className="b-modal">
          {modalItem ? (
            <div className="container-fluid marketplace-view-item-modal-container">
              <ItemViewContent item={modalItem} marketplace={marketplace} />
            </div>
          ) : (
            ''
          )}
        </div>
      </Modal>
    </div>
  );
};

MarketplaceAdminView.propTypes = {
  currentUser: PropTypes.object,
  items: PropTypes.array,
  marketplace: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.object,
};

MarketplaceAdminView.defaultProps = {};

function mapStateToProps(state) {
  return {
    currentUser: state.currentUser.user,
    items: state.marketplace.items,
    marketplace: state.marketplace.marketplace,
    loading: state.marketplace.loading,
    error: state.marketplace.error,
  };
}

const mapDispatchToProps = {
  getItems,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(MarketplaceAdminView));
